import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Hero } from "@/components/Hero";
import { CultureSection } from "@/components/CultureSection";
import { GrowthSection } from "@/components/GrowthSection";
import { GeopoliticsSection } from "@/components/GeopoliticsSection";
import { NewsletterSection } from "@/components/Newsletter";
import { FestivalBanner } from "@/components/FestivalBanner";
import { toast } from "sonner";
import { ArticlesGrid } from "@/components/ArticlesGrid";
import { SearchBox } from "@/components/SearchBox";

export default function Home() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/articles", { params: { limit: 7 } })
      .then((r) => setArticles(r.data))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("newsletter");
    if (status) {
      const msgs = {
        confirmed: ["Subscription confirmed! You'll get a note when a new article is published.", "success"],
        unsubscribed: ["You have been unsubscribed. Dhanyavaad for reading.", "success"],
        invalid: ["That link is invalid or has already been used.", "error"],
      };
      const [msg, kind] = msgs[status] || msgs.invalid;
      setTimeout(() => toast[kind](msg, { duration: 8000 }), 300);
      window.history.replaceState({}, "", "/");
    }
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
    }
  }, []);

  const navigate = useNavigate();
  const [featured, ...rest] = articles;

  return (
    <div className="relative" data-testid="home-page">
      <FestivalBanner />
      <Navbar />
      <main>
        <Hero featured={featured} />

        <section className="relative z-10 py-16" data-testid="latest-articles-section">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="eyebrow text-[var(--chakra)]">ताज़ा लेख</p>
                <h2 className="mt-2 font-serif text-3xl font-bold tracking-tight text-[var(--chakra-deep)] sm:text-4xl">Latest Articles</h2>
              </div>
              <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
                <SearchBox value="" onChange={(v) => v && navigate(`/articles?q=${encodeURIComponent(v)}`)} className="w-full sm:w-80" />
                <Link to="/articles" data-testid="view-all-articles-link" className="group inline-flex items-center gap-2 text-sm font-semibold text-[var(--saffron-deep)]">
                  View all <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
            <div className="mt-10">
              <ArticlesGrid articles={rest.slice(0, 6)} loading={loading} />
            </div>
          </div>
        </section>

        <CultureSection />
        <GrowthSection />
        <GeopoliticsSection />
        <NewsletterSection />
      </main>
      <Footer />
    </div>
  );
}
